import type { BatchErrors as BatchErrorsData } from '@reach/shared-types/partner-batches';
import React from 'react';
import styles from './BatchErrors.module.css';

const LABELS = {
  errorMessage: 'Error Message',
  errorSummary: 'Error Summary',
  failedRecords: 'Failed Records',
  record: 'Record',
  categories: 'Categories',
} as const;

interface BatchErrorsProps {
  errors: BatchErrorsData;
}

/**
 * Renders the error breakdown for a batch: the batch-level message, the per-category
 * counts, and the record-level messages that name the offending fields.
 */
export const BatchErrors: React.FC<BatchErrorsProps> = ({ errors }) => {
  const categoryCounts = Object.entries(errors.summary ?? {});
  const failedRecords = Object.entries(errors.details ?? {});
  // recordErrors and details describe the same failures at different granularity —
  // details keyed by record, recordErrors as row-level messages. Showing both would
  // print every message twice, so recordErrors only fills in when details is absent
  // (batch types that report row messages without per-record grouping).
  const rowMessages = failedRecords.length > 0 ? [] : (errors.recordErrors ?? []);

  return (
    <div className={styles.batchErrors}>
      {errors.message && (
        <section>
          <h4>{LABELS.errorMessage}</h4>
          <p className={styles.errorMessage}>{errors.message}</p>
        </section>
      )}

      {categoryCounts.length > 0 && (
        <section>
          <h4>{LABELS.errorSummary}</h4>
          <ul className={styles.categoryCounts}>
            {categoryCounts.map(([category, count]) => (
              <li key={category}>
                {category}
                <b>{count}</b>
              </li>
            ))}
          </ul>
        </section>
      )}

      {failedRecords.length > 0 && (
        <section>
          <h4>{LABELS.failedRecords}</h4>
          <ul className={styles.failedRecords}>
            {failedRecords.map(([recordKey, recordFailure]) => (
              <li key={recordKey}>
                <p className={styles.recordKey}>
                  {LABELS.record}: {recordKey}
                </p>
                {recordFailure.categories.length > 0 && (
                  <p className={styles.recordCategories}>
                    {LABELS.categories}: {recordFailure.categories.join(', ')}
                  </p>
                )}
                <ul className={styles.recordMessages}>
                  {recordFailure.errors.map((message, index) => (
                    <li key={`${recordKey}-${index}`}>{message}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rowMessages.length > 0 && (
        <section>
          <h4>{LABELS.failedRecords}</h4>
          <ul className={styles.rowMessages}>
            {rowMessages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
